import { useCallback, useEffect, useRef, useState } from "react";
import { BarChart3 } from "lucide-react";
import { Navigate } from "react-router-dom";
import { api, useAuth } from "@/auth/AuthContext";
import { toast } from "@/components/ui/sonner";
import { MantabMetrics } from "@/components/mantab/MantabMetrics";
import { MantabStudentRegistry } from "@/components/mantab/MantabStudentRegistry";
import { MantabBankStudentRegistry } from "@/components/mantab/MantabBankStudentRegistry";

export default function MantabDashboard() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [error, setError] = useState("");
  const bankAccessRequest = useRef(0);
  const persistedBankAccess = useRef("");
  const latestBankAccess = useRef("");
  const load = useCallback(async () => {
    const metricsYear = selectedYear === "__all__" ? "" : selectedYear;
    try { setMetrics((await api.get("/mantab/metrics", { params: { year: metricsYear } })).data); }
    catch (requestError) { setError(requestError.response?.data?.detail || "Data distribusi belum dapat dimuat."); }
  }, [selectedYear]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!["master", "admin", "pt"].includes(user?.role)) return;
    api.get("/mantab/filters").then((response) => setYears(response.data.years || [])).catch((requestError) => setError(requestError.response?.data?.detail || "Pilihan Tahun Penetapan tidak dapat dimuat."));
  }, [user?.role]);
  useEffect(() => {
    if (!["master", "admin", "bank_admin"].includes(user?.role)) return;
    const requestId = ++bankAccessRequest.current;
    api.get("/mantab/bank-student-access").then((response) => {
      if (requestId === bankAccessRequest.current) {
        const savedSelection = response.data.selection || "";
        persistedBankAccess.current = savedSelection;
        latestBankAccess.current = savedSelection;
        setSelectedYear(savedSelection);
        if (response.data.years?.length) setYears(response.data.years);
      }
    }).catch((requestError) => {
      if (requestId === bankAccessRequest.current) setError(requestError.response?.data?.detail || "Tahun akses mahasiswa Bank tidak dapat dimuat.");
    });
    return () => { if (requestId === bankAccessRequest.current) bankAccessRequest.current += 1; };
  }, [user?.role]);
  const changeYear = async (value) => {
    const requestId = ++bankAccessRequest.current;
    latestBankAccess.current = value;
    setSelectedYear(value);
    if (!["master", "admin"].includes(user?.role)) return;
    try {
      await api.put("/mantab/bank-student-access", { selection: value });
      if (requestId !== bankAccessRequest.current) {
        await api.put("/mantab/bank-student-access", { selection: latestBankAccess.current });
        return;
      }
      persistedBankAccess.current = value;
      toast.success("Akses Daftar Mahasiswa seluruh Bank diperbarui.");
    } catch (requestError) {
      if (requestId === bankAccessRequest.current) {
        latestBankAccess.current = persistedBankAccess.current;
        setSelectedYear(persistedBankAccess.current);
        setError(requestError.response?.data?.detail || "Tahun akses mahasiswa Bank gagal disimpan.");
      }
    }
  };
  if (user?.role === "bank_operator") return <Navigate to="/mantab/kelola-distribusi" replace />;
  const registryYear = selectedYear === "__all__" ? "" : selectedYear;
  return <div className="mantab-page" data-testid="mantab-dashboard">
    <section className="mantab-page-head"><div><div className="mantab-kicker"><BarChart3 size={14} /> DATA DISTRIBUSI BUKU TABUNGAN</div><h1>Data Distribusi</h1></div></section>
    {error && <div className="mantab-alert error" data-testid="mantab-dashboard-error">{error}</div>}
    <MantabMetrics metrics={metrics} years={years} selectedYear={selectedYear} onYearChange={["master", "admin", "pt"].includes(user?.role) ? changeYear : undefined} controlsBankAccess={["master", "admin"].includes(user?.role)} />
    <section className="mantab-next-action" data-testid="mantab-distribution-guidance"><div><strong>Pengelolaan Distribusi</strong><span>Untuk memasukkan data nomor rekening, silahkan pilih mahasiswa dengan filter status buku rekening 'belum dibuatkan rekening', kemudian unduh. Lengkapi data unduhan dengan nomor rekening mahasiswa dan data lainnya (jika ada) kemudian unggah kembali data tersebut ke platform ini</span></div></section>
    {user?.role === "bank_admin" && <MantabBankStudentRegistry />}
    {!['bank_admin', 'bank_operator'].includes(user?.role) && <MantabStudentRegistry selectedYear={registryYear} onChanged={load} />}
  </div>;
}